import React from "react";
import { Link } from "react-router-dom";
import {
  Phone, MessageCircle, ArrowRight, Star, MapPin,
  ShieldCheck, GraduationCap, Sparkles, Microscope, HeartHandshake, ClipboardCheck,
  Sparkles as SparkIcon, Focus, Wand2, Eye,
  Cpu, Activity, ScanSearch, Grid3x3, Camera, CircleDot, Home as HomeIcon, AlertCircle,
} from "lucide-react";
import { HOME_VISIT_WA } from "@/pages/HomeVisit";
import Seo from "@/components/Seo";
import { JsonLd, reviewsSchema } from "@/components/StructuredData";
import { CLINIC } from "@/lib/clinicInfo";
import { SERVICES } from "@/data/services";
import AppointmentForm from "@/components/AppointmentForm";
import {
  Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext,
} from "@/components/ui/carousel";

const PILLARS = [
  { title: "Cataract & Premium IOLs", desc: "Micro-incision phaco with monofocal, toric, multifocal and trifocal IOLs.", icon: <SparkIcon className="h-5 w-5" />, path: "/services/cataract-surgery" },
  { title: "Oculoplasty & Periocular Care", desc: "Blepharoplasty, ptosis, DCR, tear-trough fillers and precision botulinum toxin injections around the eyes.", icon: <Wand2 className="h-5 w-5" />, path: "/services/oculoplasty-eyelid-surgery" },
  { title: "Cornea & Refractive Care", desc: "LASIK, SMILE and ICL evaluation, dry-eye workups and corneal disease.", icon: <Focus className="h-5 w-5" />, path: "/services/refractive-surgery" },
  { title: "Comprehensive Ophthalmology", desc: "Full-spectrum eye examination, glaucoma, retina screening and paediatric care.", icon: <Eye className="h-5 w-5" />, path: "/services/comprehensive-examination" },
];

const WHY = [
  { icon: <GraduationCap className="h-5 w-5" />, title: "Fellowship-Trained", desc: "MBBS (AFMC Pune), MS Ophthalmology (KGMU), fellowship at LVPEI Hyderabad, aesthetic training under Dr. Ramtin Kassir (New York)." },
  { icon: <HeartHandshake className="h-5 w-5" />, title: "Personally Led Care", desc: "Every consultation, every surgery, every follow-up is with Dr. Gurkiran Kaur — no rotating juniors, no hand-offs." },
  { icon: <Microscope className="h-5 w-5" />, title: "Modern OT & Diagnostics", desc: "Advanced phacoemulsification, surgical microscope, OCT, biometry, topography and perimetry — all under one roof." },
  { icon: <ClipboardCheck className="h-5 w-5" />, title: "Evidence-Based Advice", desc: "Honest recommendations. If you don't need surgery, we'll say so. If you do, we'll explain every option in plain English." },
  { icon: <ShieldCheck className="h-5 w-5" />, title: "Cashless Insurance", desc: "End-to-end paperwork support for 30+ major insurers and TPAs — including for cataract and day-care procedures." },
  { icon: <Sparkles className="h-5 w-5" />, title: "Aesthetic Sensibility", desc: "Fellowship-trained oculoplasty ensures periocular botulinum toxin, fillers and lid surgery look natural, not overdone." },
];

const TECH = [
  { icon: <Cpu className="h-5 w-5" />, name: "Advanced Phacoemulsification", desc: "Sutureless 2.2 mm micro-incision cataract surgery" },
  { icon: <ScanSearch className="h-5 w-5" />, name: "Optical Biometry (IOL Master)", desc: "Precision IOL power calculation for premium lenses" },
  { icon: <Grid3x3 className="h-5 w-5" />, name: "Corneal Topography", desc: "Refractive planning, keratoconus and post-LASIK screening" },
  { icon: <CircleDot className="h-5 w-5" />, name: "OCT Imaging", desc: "Retina and optic nerve fibre layer analysis" },
  { icon: <Activity className="h-5 w-5" />, name: "Automated Perimetry", desc: "Glaucoma diagnosis and long-term monitoring" },
  { icon: <Camera className="h-5 w-5" />, name: "Digital Slit Lamp", desc: "High-resolution anterior segment documentation" },
];

const HV_TESTS = ["Portable Slit Lamp", "Undilated Fundus Photo", "Vision & Refraction", "Virtual Consult with Dr. Kaur"];

function HomeVisitBand() {
  return (
    <section className="container-wide pt-20" data-testid="home-visit-band">
      <div className="rounded-3xl bg-primary text-primary-foreground overflow-hidden grid lg:grid-cols-12">
        <div className="lg:col-span-7 p-8 sm:p-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest opacity-80 mb-4"><HomeIcon className="h-4 w-4" /> New · Home Eye Check-up</div>
          <h2 className="font-serif text-3xl sm:text-5xl leading-[1.05]">Can't come to the clinic? <span className="italic opacity-90">We'll come to you.</span></h2>
          <p className="mt-5 opacity-85 leading-7 max-w-xl">Specialist eye check-ups in the comfort of your home across Noida &amp; Greater Noida — for elderly or bedridden patients, those recovering after surgery, people with disabilities and busy professionals.</p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-2 text-sm">
            {HV_TESTS.map((t) => <li key={t} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-accent" />{t}</li>)}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/eye-checkup-at-home" className="inline-flex items-center gap-2 rounded-full bg-background text-primary px-6 py-3 text-sm font-medium hover:bg-background/90" data-testid="home-visit-band-learn">Explore Home Visits <ArrowRight className="h-4 w-4" /></Link>
            <a href={HOME_VISIT_WA} target="_blank" rel="noopener noreferrer" data-track-location="Home visit WhatsApp (homepage)" className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/40 px-6 py-3 text-sm hover:bg-primary-foreground/10" data-testid="home-visit-band-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp for Home Visit</a>
          </div>
          <p className="mt-6 text-xs opacity-75 flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5" /> Not recommended for children under 15 yrs · Call or WhatsApp for charges</p>
        </div>
        <div className="lg:col-span-5 min-h-[260px]">
          <img src="/images/home-eye-checkup.jpg" alt="Doctor conducting a home eye check-up for an elderly patient in Noida" className="w-full h-full object-cover" loading="lazy" />
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Advanced Eye Care in Noida — Personally Led by Dr. Gurkiran Kaur"
        description="Specialist ophthalmic care in Sector 32 Noida — advanced cataract surgery, oculoplasty, cornea and comprehensive eye care by Dr. Gurkiran Kaur."
        path="/"
      />
      <JsonLd data={reviewsSchema(CLINIC, CLINIC.testimonials)} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="container-wide pt-14 sm:pt-20 pb-16 sm:pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-xs uppercase tracking-widest text-primary mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              Sector 32, Wave City Centre · Noida
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05] tracking-tight">
              Advanced Eye Care.<br />
              <span className="text-primary italic">Personally Led.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-foreground/75 max-w-xl leading-7">
              Specialist ophthalmic care in Noida, led by <strong className="text-foreground">Dr. Gurkiran Kaur</strong>, with expertise in advanced cataract surgery, oculoplasty and comprehensive eye care.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:bg-primary/90" data-testid="hero-book-btn">
                Book an Appointment <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={`tel:${CLINIC.phoneRaw}`} className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-medium hover:bg-secondary" data-testid="hero-call-btn">
                <Phone className="h-4 w-4" /> Call {CLINIC.phone}
              </a>
              <a href={CLINIC.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full px-4 py-3 text-sm text-primary hover:opacity-80" data-testid="hero-whatsapp-btn">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-accent/10 rounded-3xl -z-10 rotate-2" />
              <div className="rounded-2xl overflow-hidden border border-border bg-card shadow-sm">
                <img src={CLINIC.doctorPhoto} alt="Dr. Gurkiran Kaur - Fellowship Trained Ophthalmologist at Nehchal Eye Centre Noida" className="w-full h-[440px] object-cover object-top" loading="eager" width="480" height="440" />
                <div className="p-6 bg-card">
                  <div className="text-xs uppercase tracking-widest text-accent">Founder & Director</div>
                  <div className="font-serif text-2xl text-foreground mt-1">Dr. Gurkiran Kaur</div>
                  <div className="text-sm text-muted-foreground">MS Ophthalmology · Fellowship Trained (LVPEI)</div>
                  <div className="mt-3 flex items-center gap-1 text-amber-500">
                    {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                    <span className="text-xs text-muted-foreground ml-2">{CLINIC.ratingValue} · {CLINIC.reviewCount}+ Google reviews</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="border-y border-border bg-secondary/40" data-testid="home-pillars">
        <div className="container-wide py-14">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">Specialist Care Across</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {PILLARS.map((p) => (
              <Link key={p.title} to={p.path} className="group rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all p-5" data-testid={`pillar-${p.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">{p.icon}</div>
                <div className="font-serif text-lg text-foreground group-hover:text-primary transition-colors">{p.title}</div>
                <p className="mt-2 text-sm text-foreground/65 leading-6">{p.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <HomeVisitBand />

      {/* WHY NEHCHAL */}
      <section className="container-wide py-20" data-testid="home-why">
        <div className="max-w-2xl mb-12">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">Why Nehchal?</div>
          <h2 className="font-serif text-3xl sm:text-5xl text-foreground">Six reasons patients choose us.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {WHY.map((w) => (
            <div key={w.title} className="rounded-xl border border-border bg-card p-6" data-testid={`why-${w.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">{w.icon}</div>
              <div className="font-serif text-lg">{w.title}</div>
              <p className="text-sm text-foreground/70 mt-2 leading-6">{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DR PROFILE */}
      <section className="bg-secondary/40 border-y border-border py-20" data-testid="home-doctor">
        <div className="container-wide grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden border border-border bg-card">
              <img src={CLINIC.doctorPhoto} alt="Dr. Gurkiran Kaur, Founder of Nehchal Eye Centre Noida" className="w-full h-[420px] object-cover object-top" loading="lazy" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Meet Your Surgeon</div>
            <h2 className="font-serif text-3xl sm:text-5xl text-foreground leading-[1.05]">Dr. Gurkiran Kaur</h2>
            <p className="mt-3 text-primary">Founder & Director · Fellowship Trained Ophthalmologist</p>
            <p className="mt-6 text-foreground/80 leading-8">
              Trained at some of India&apos;s finest institutions and mentored under internationally renowned facial plastic surgeon Dr. Ramtin Kassir in New York, Dr. Kaur brings surgical precision and an aesthetic eye to every consultation.
            </p>
            <p className="mt-4 text-foreground/80 leading-8">
              Her personal experience spans advanced cataract surgery, oculoplasty and periocular aesthetics, dry-eye and comprehensive ophthalmology — with a philosophy of tailor-made, evidence-based care.
            </p>
            <div className="mt-8">
              <Link to="/about" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90" data-testid="doctor-read-more">
                Read full profile <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-wide py-20" data-testid="home-testimonials">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Patient Voices</div>
            <h2 className="font-serif text-3xl sm:text-5xl text-foreground">What our patients say.</h2>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-foreground/70">
              <span className="flex items-center gap-0.5 text-amber-500">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </span>
              <span className="font-medium text-foreground">{CLINIC.ratingValue}</span>
              <span>·</span>
              <a href={CLINIC.googleMapsCid} target="_blank" rel="noopener noreferrer" className="text-primary hover:opacity-80 underline underline-offset-4" data-testid="google-reviews-link">
                Read all {CLINIC.reviewCount}+ reviews on Google
              </a>
            </div>
          </div>
          <div className="text-sm text-muted-foreground hidden sm:block">Swipe or use the arrows →</div>
        </div>
        <Carousel opts={{ align: "start", loop: true }} className="w-full" data-testid="testimonials-carousel">
          <CarouselContent className="-ml-4">
            {CLINIC.testimonials.map((t) => (
              <CarouselItem key={t.name} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3" data-testid={`testimonial-${t.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <blockquote className="h-full rounded-xl border border-border bg-card p-6 flex flex-col">
                  <div className="flex items-center gap-1 text-amber-500 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                  </div>
                  <p className="text-sm text-foreground/80 leading-6 italic flex-1">&ldquo;{t.text}&rdquo;</p>
                  <footer className="mt-6 pt-4 border-t border-border/60 text-sm">
                    <div className="font-medium text-foreground">{t.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{t.role}</div>
                    <div className="text-[10px] uppercase tracking-widest text-primary mt-2">
                      {t.source === "Google" ? "★ Verified · Google Review" : "Verified Patient"}
                    </div>
                  </footer>
                </blockquote>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-end gap-2 mt-6">
            <CarouselPrevious className="static translate-y-0" data-testid="testimonials-prev" />
            <CarouselNext className="static translate-y-0" data-testid="testimonials-next" />
          </div>
        </Carousel>
      </section>

      {/* TECHNOLOGY */}
      <section className="bg-secondary/40 border-y border-border py-20" data-testid="home-technology">
        <div className="container-wide">
          <div className="max-w-2xl mb-10">
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Technology</div>
            <h2 className="font-serif text-3xl sm:text-5xl text-foreground">Diagnostics and surgery — under one roof.</h2>
            <p className="mt-4 text-foreground/70 leading-7">
              Modern eye care depends as much on the equipment as on the surgeon. Every diagnostic and surgical tool below is available at our Sector 32 Noida facility.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TECH.map((x) => (
              <div key={x.name} className="rounded-xl border border-border bg-card p-5 flex gap-4 items-start" data-testid={`tech-${x.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">{x.icon}</div>
                <div>
                  <div className="font-serif text-base text-foreground">{x.name}</div>
                  <p className="text-xs sm:text-sm text-foreground/65 mt-1 leading-6">{x.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONDITIONS / SERVICES */}
      <section className="container-wide py-20" data-testid="home-services">
        <div className="max-w-2xl mb-12">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">Conditions & Services</div>
          <h2 className="font-serif text-3xl sm:text-5xl text-foreground">Complete eye care under one roof.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} className="group block rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all p-6" data-testid={`home-service-card-${s.slug}`}>
              <div className="font-serif text-lg text-foreground group-hover:text-primary transition-colors">{s.name}</div>
              <p className="mt-2 text-sm text-foreground/65 leading-6">{s.short}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                Learn more <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* LOCATION + APPOINTMENT */}
      <section className="container-wide pb-24" data-testid="home-appointment">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Visit Us</div>
            <h2 className="font-serif text-3xl sm:text-5xl text-foreground mb-6">Book your consultation.</h2>
            <p className="text-foreground/70 leading-7 mb-6">
              Fill out the form and our team will confirm your slot. For urgent concerns please call us directly.
            </p>
            <div className="rounded-xl border border-border bg-card p-4 flex items-start gap-3 mb-4">
              <MapPin className="h-5 w-5 text-primary mt-0.5" />
              <div className="text-sm">
                <div className="font-medium">{CLINIC.address.line1}</div>
                <div className="text-muted-foreground">{CLINIC.address.line2}, {CLINIC.address.state}</div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-border bg-card">
              <iframe
                title="Nehchal Eye Centre Location Map"
                src={CLINIC.mapsEmbedUrl}
                className="w-full h-64 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={`tel:${CLINIC.phoneRaw}`} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90" data-testid="home-call-cta">
                <Phone className="h-4 w-4" /> Call {CLINIC.phone}
              </a>
              <a href={CLINIC.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm hover:bg-secondary" data-testid="home-whatsapp-cta">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
            <AppointmentForm />
          </div>
        </div>
      </section>
    </div>
  );
}
